import { useCallback, useEffect, useState } from "react";
import { ShieldCheck, ShieldOff, KeyRound, AlertCircle, CheckCircle } from "lucide-react";
import { PinStatus, PIN_RE, pinRequest, onlyDigits } from "../security";

const inputClass = "w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 text-base tracking-widest";

function PinInput({ label, value, onChange, autoFocus }: { label: string; value: string; onChange: (v: string) => void; autoFocus?: boolean }) {
    return (
        <label className="block">
            <span className="block text-sm font-medium text-gray-700 mb-1.5">{label}</span>
            <input
                type="password"
                inputMode="numeric"
                autoComplete="off"
                maxLength={6}
                placeholder="••••••"
                value={value}
                autoFocus={autoFocus}
                onChange={(e) => onChange(onlyDigits(e.target.value))}
                className={inputClass}
            />
        </label>
    );
}

// Cài đặt mã bảo mật ví (6 số): bật, đổi mã, tắt. Quên mã thì liên hệ admin để hủy.
export function SecurityPinSettings() {
    const [status, setStatus] = useState<PinStatus | null>(null);
    const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
    const [busy, setBusy] = useState(false);
    const [mode, setMode] = useState<"change" | "disable" | null>(null);
    const [form, setForm] = useState({ password: "", pin: "", confirm: "", current: "" });

    const load = useCallback(async () => {
        const res = await pinRequest("");
        if (res.ok) setStatus(res.data as PinStatus);
        else setMessage({ type: "error", text: res.data.message || "Không tải được trạng thái mã bảo mật" });
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const reset = () => setForm({ password: "", pin: "", confirm: "", current: "" });

    const submit = async (path: string, body: object, validate?: () => string | null) => {
        const err = validate?.();
        if (err) return setMessage({ type: "error", text: err });
        setBusy(true);
        setMessage(null);
        const res = await pinRequest(path, body);
        setBusy(false);
        if (!res.ok) return setMessage({ type: "error", text: res.data.message || "Thao tác thất bại" });
        setMessage({ type: "success", text: res.data.message || "Thành công" });
        reset();
        setMode(null);
        setStatus(res.data as PinStatus);
    };

    const newPinError = () => {
        if (!PIN_RE.test(form.pin)) return "Mã bảo mật phải gồm đúng 6 chữ số";
        if (form.pin !== form.confirm) return "Mã nhập lại không khớp";
        return null;
    };

    return (
        <div className="space-y-6">
            <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4 text-sm text-cyan-900 space-y-1">
                <p className="font-semibold">Mã bảo mật ví là gì?</p>
                <p>Khi bật, ví của bạn luôn ở trạng thái <strong>khóa</strong>. Trước khi lấy xe ra khoảng 15 phút, hãy mở Trang chủ và nhập mã 6 số để mở khóa ví trong 15 phút, khi đó phí gửi xe được trừ tự động như bình thường.</p>
                <p>Nếu ví đang khóa, cổng ra sẽ không tự thanh toán và không mở barrier, giúp chống người khác lấy xe của bạn.</p>
            </div>

            {message && (
                <div className={`p-3 rounded-lg flex items-center gap-2 text-sm ${message.type === "success" ? "bg-green-50 text-green-700 border border-green-200" : "bg-red-50 text-red-700 border border-red-200"}`}>
                    {message.type === "success" ? <CheckCircle className="h-4 w-4 flex-shrink-0" /> : <AlertCircle className="h-4 w-4 flex-shrink-0" />}
                    <span>{message.text}</span>
                </div>
            )}

            {!status ? (
                <p className="text-gray-500 text-sm">Đang tải...</p>
            ) : !status.enabled ? (
                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        submit("/setup", { password: form.password, pin: form.pin }, () => newPinError() || (!form.password ? "Nhập mật khẩu tài khoản để xác nhận" : null));
                    }}
                    className="space-y-4 max-w-md"
                >
                    <div className="flex items-center gap-2 text-gray-700">
                        <ShieldOff className="h-5 w-5 text-gray-400" />
                        <span>Mã bảo mật đang <strong>tắt</strong>: xe ra vào được tự trừ phí.</span>
                    </div>
                    <PinInput label="Mã bảo mật mới (6 số)" value={form.pin} onChange={(pin) => setForm({ ...form, pin })} />
                    <PinInput label="Nhập lại mã bảo mật" value={form.confirm} onChange={(confirm) => setForm({ ...form, confirm })} />
                    <label className="block">
                        <span className="block text-sm font-medium text-gray-700 mb-1.5">Mật khẩu tài khoản</span>
                        <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputClass.replace(" tracking-widest", "")} />
                    </label>
                    <button type="submit" disabled={busy} className="w-full bg-cyan-600 hover:bg-cyan-700 disabled:opacity-60 text-white rounded-lg px-4 py-2.5 font-medium">
                        {busy ? "Đang xử lý..." : "Bật mã bảo mật"}
                    </button>
                </form>
            ) : (
                <div className="space-y-4 max-w-md">
                    <div className="flex items-center gap-2 text-gray-700">
                        <ShieldCheck className="h-5 w-5 text-emerald-600" />
                        <span>Mã bảo mật đang <strong className="text-emerald-700">bật</strong>. Ví hiện {status.locked ? <strong className="text-red-600">đang khóa</strong> : <strong className="text-emerald-700">đang mở</strong>}.</span>
                    </div>

                    {mode === null && (
                        <div className="flex flex-col sm:flex-row gap-3">
                            <button onClick={() => { setMode("change"); setMessage(null); }} className="flex-1 inline-flex items-center justify-center gap-2 border border-cyan-600 text-cyan-700 hover:bg-cyan-50 rounded-lg px-4 py-2.5 font-medium">
                                <KeyRound className="h-4 w-4" /> Đổi mã bảo mật
                            </button>
                            <button onClick={() => { setMode("disable"); setMessage(null); }} className="flex-1 inline-flex items-center justify-center gap-2 border border-red-300 text-red-600 hover:bg-red-50 rounded-lg px-4 py-2.5 font-medium">
                                <ShieldOff className="h-4 w-4" /> Tắt mã bảo mật
                            </button>
                        </div>
                    )}

                    {mode === "change" && (
                        <form onSubmit={(e) => { e.preventDefault(); submit("/change", { current_pin: form.current, new_pin: form.pin }, newPinError); }} className="space-y-4">
                            <PinInput label="Mã hiện tại" value={form.current} onChange={(current) => setForm({ ...form, current })} autoFocus />
                            <PinInput label="Mã mới (6 số)" value={form.pin} onChange={(pin) => setForm({ ...form, pin })} />
                            <PinInput label="Nhập lại mã mới" value={form.confirm} onChange={(confirm) => setForm({ ...form, confirm })} />
                            <div className="flex gap-3">
                                <button type="button" onClick={() => { setMode(null); reset(); }} className="flex-1 border border-gray-300 rounded-lg px-4 py-2.5 text-gray-700 hover:bg-gray-50">Hủy</button>
                                <button type="submit" disabled={busy} className="flex-1 bg-cyan-600 hover:bg-cyan-700 disabled:opacity-60 text-white rounded-lg px-4 py-2.5 font-medium">{busy ? "Đang lưu..." : "Lưu mã mới"}</button>
                            </div>
                        </form>
                    )}

                    {mode === "disable" && (
                        <form onSubmit={(e) => { e.preventDefault(); submit("/disable", { pin: form.current }, () => (PIN_RE.test(form.current) ? null : "Nhập mã bảo mật hiện tại (6 số)")); }} className="space-y-4">
                            <p className="text-sm text-gray-600">Sau khi tắt, ví sẽ tự trừ phí khi xe ra mà không cần mở khóa.</p>
                            <PinInput label="Mã bảo mật hiện tại" value={form.current} onChange={(current) => setForm({ ...form, current })} autoFocus />
                            <div className="flex gap-3">
                                <button type="button" onClick={() => { setMode(null); reset(); }} className="flex-1 border border-gray-300 rounded-lg px-4 py-2.5 text-gray-700 hover:bg-gray-50">Hủy</button>
                                <button type="submit" disabled={busy} className="flex-1 bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white rounded-lg px-4 py-2.5 font-medium">{busy ? "Đang xử lý..." : "Tắt mã bảo mật"}</button>
                            </div>
                        </form>
                    )}

                    <p className="text-sm text-gray-500">Quên mã bảo mật? Hãy liên hệ quản trị viên để được hủy mã, sau đó bạn có thể cài mã mới.</p>
                </div>
            )}
        </div>
    );
}
